<template>
  <v-app id="app">
    <!-- Header -->
    <v-app-bar app color="surface" elevation="2">
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" />
      <v-toolbar-title>
        <img  src="./assets/logo-1657285507.jpg" height="40px" style="padding-top: 6px;" >
      </v-toolbar-title>
    </v-app-bar>

    <!-- Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" app>
      <!-- Navigation items -->
      <v-list class="pt-4">
        <v-list-item
          v-for="view in mainViews"
          :key="view.path"
          :prepend-icon="view.icon"
          :title="view.title"
          :to="view.path"
          link
          color="primary"
          style="cursor: pointer;"
        />
        <v-list-group value="sales">
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-cash-register"
              title="Sales"
              color="primary"
              @click="router.push('/sales/dashboard')"
            />
          </template>
          <v-list-item
            title="Dashboard"
            to="/sales/dashboard"
            link
            color="primary"
            style="cursor: pointer;"
          />
          <v-list-item
            title="Import and Review"
            to="/sales/import-and-review"
            link
            color="primary"
            style="cursor: pointer;"
          />
        </v-list-group>
        <v-list-item
          :prepend-icon="settingsView.icon"
          :title="settingsView.title"
          :to="settingsView.path"
          link
          color="primary"
          style="cursor: pointer;"
        />
      </v-list>

      <!-- About button at bottom -->
      <template v-slot:append>
        <v-divider></v-divider>
        <v-list class="pt-2">
          <v-list-item
            :prepend-icon="aboutView.icon"
            :title="aboutView.title"
            :to="aboutView.path"
            link
            color="primary"
            style="cursor: pointer;"
          />
          <v-list-item
            v-if="statusLoggedIn"
            class="mt-2 text-error"
            prepend-icon="mdi-logout"
            title="Logout"
            @click="logout"
            style="cursor: pointer;"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Body / Content -->
    <v-main>
      <router-view></router-view>
    </v-main>

    <!-- Footer -->
    <v-footer app color="secondary" elevation="2">&copy; Tjoef Tjaf 2022</v-footer>
  </v-app>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'App',
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const drawer = ref(false);

    const mainViews = [
      { title: "Home", icon: "mdi-home", path: "/home" },
      { title: "List", icon: "mdi-playlist-plus", path: "/list" },
      { title: "Listing Sessions", icon: "mdi-format-list-bulleted", path: "/listing-sessions" },
      { title: "Inventory", icon: "mdi-warehouse", path: "/inventory" },
      { title: "Images", icon: "mdi-image-multiple", path: "/images" },
    ];
    const settingsView = { title: "Settings", icon: "mdi-account-cog", path: "/settings" };

    const aboutView = { title: "About", icon: "mdi-information", path: "/about" };

    const statusLoggedIn = computed(() => store.getters["auth/getStatusLoggedIn"]);

    const logout = () => {
      store.dispatch("auth/logout")
        .then(() => {
          console.log("logged out");
          router.push("/login");
        })
        .catch(err => console.log(err));
    };

    watch(
      () => route.query.openNav,
      async (openNavFlag) => {
        if (!openNavFlag) return;
        drawer.value = true;
        const nextQuery = { ...route.query };
        delete nextQuery.openNav;
        await router.replace({ path: route.path, query: nextQuery });
      },
      { immediate: true }
    );

    return {
      drawer,
      mainViews,
      settingsView,
      aboutView,
      statusLoggedIn,
      logout,
      router
    };
  }
})
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Roboto&family=Ubuntu&display=swap");

$body-font-family: "Ubuntu";

$typoOptions: display-4, display-3, display-2, display-1, headline, title,
  subtitle-1, subtitle-2, body-1, body-2, caption, overline;

#app {
  font-family: $body-font-family, "Roboto", Arial, Helvetica, sans-serif !important;
  @each $typoOption in $typoOptions {
    .#{$typoOption} {
      font-family: $body-font-family, sans-serif !important;
    }
  }

  background-color: var(--brand-background);
}

html {
  background-color: var(--brand-background);
}

.v-list-item-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 16px;
}

.v-btn {
  text-transform: none;
}

/* Shared content card style – edit this to change card look app-wide.
   !important overrides Vuetify v-card defaults. */
#app .v-card.content-card {
  border: 1px solid rgba(15, 23, 42, 0.18) !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 20px rgba(15, 23, 42, 0.1) !important;
}

/* Shared card header – use on v-card-title or v-card-item for consistent spacing.
   Bottom padding matches the headroom created by header actions (e.g. Reset button) in ItemSearch. */
#app .v-card.content-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 16px !important;
  flex-wrap: wrap;
}

/* Header actions (e.g. Reset button) are positioned absolutely so they don't affect layout.
   Use card-header--with-action on the header and card-header__action on the button. */
#app .v-card.content-card .card-header--with-action {
  position: relative;
  padding-right: 100px !important;
}

#app .v-card.content-card .card-header__action {
  position: absolute;
  right: 20px;
  top: 20px;
}

/* Shared card title – use for the main title text inside card-header. */
#app .v-card.content-card .card-title {
  font-size: 1.1rem;
  line-height: 1.3;
  font-weight: 600;
  color: rgba(12, 22, 40, 0.95);
  padding: 0 !important;
  margin: 0;
}

/* Optional subtitle below card-title. */
#app .v-card.content-card .card-subtitle {
  margin-top: 2px;
  font-size: 0.88rem;
  color: rgba(15, 23, 42, 0.62);
}

/* Shared card body – use on v-card-text for consistent horizontal padding. */
#app .v-card.content-card .card-body {
  padding: 0 20px 20px !important;
}

@media (max-width: 600px) {
  #app .v-card.content-card .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  /* On mobile, header actions flow normally instead of absolute positioning */
  #app .v-card.content-card .card-header--with-action {
    padding-right: 20px !important;
  }

  #app .v-card.content-card .card-header__action {
    position: static;
  }
}

</style>
